import { MenuItem as MuiMenuItem, menuItemClasses as MuiMenuItemClasses, Stack, SvgIconProps } from '@mui/material';
import React, { forwardRef } from 'react';
import { PolymorphicComponentProps, PolymorphicRef, SxProps } from '../../utils';
import { BadgeProps } from '../Badge';
import { Typography } from '../Typography';

type DefaultMenuitemRootComponent = 'li';

export interface BaseMenuItemProps {
  /**
   * The text for the menu item
   */
  children: React.ReactNode | React.ReactNode[];
  /**
   * Optional badge to be shown on the right of the menu item.
   * Recommend using size="small" for the Badge.
   */
  Badge?: React.ReactElement<BadgeProps>;
  /**
   * An optional description that helps explain the purpose of the menu item.
   * In the following example, "My Cool Collection" is the text, and
   * "This collection..." is the description.
   *
   * [ *My Cool Collection*                        ]
   * [ This collection is the best collection ever ]
   */
  description?: string | React.ReactNode;
  /**
   * Whether the menu item is disabled
   */
  disabled?: boolean;
  /**
   * An optional SVG icon that will appear to the left of the text
   */
  Icon?: React.ElementType<SvgIconProps>;
  /**
   * Whether the menu item has been selected
   */
  selected?: boolean;
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps;
}

export const menuItemClasses = {
  disabled: MuiMenuItemClasses.disabled,
  root: MuiMenuItemClasses.root,
  selected: MuiMenuItemClasses.selected,
};

export type MenuItemProps<RootComponent extends React.ElementType = DefaultMenuitemRootComponent> =
  PolymorphicComponentProps<RootComponent, BaseMenuItemProps>;

const MenuItemWithoutForwardRef = <RootComponent extends React.ElementType = DefaultMenuitemRootComponent>(
  { Badge, children, description, Icon, disabled, selected, sx, ...props }: MenuItemProps<RootComponent>,
  ref?: PolymorphicRef<RootComponent>
) => {
  return (
    <MuiMenuItem {...props} disabled={disabled} selected={selected} sx={sx} ref={ref}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
        {Icon && <Icon sx={{ fontSize: '14px' }} />}
        <Stack sx={{ width: '100%' }}>
          <Stack direction="row" justifyContent="space-between" spacing={3} sx={{ width: '100%' }}>
            <Typography variant="body1">{children}</Typography>
            {Badge && <div>{Badge}</div>}
          </Stack>
          {description && (
            <Typography
              variant="body2"
              sx={{
                whiteSpace: 'normal',
                color: (theme) =>
                  theme.featureFlags.panwRebrandStubYear
                    ? theme.palette.text.placeholder
                    : theme.palette.text.secondary,
              }}
            >
              {description}
            </Typography>
          )}
        </Stack>
      </Stack>
    </MuiMenuItem>
  );
};

// Intentionally using some `any` here to around around some type errors before the casting. We have to do some casting
// here because forwardRef, generics, and TypeScript do not play nicely together.
// https://react-typescript-cheatsheet.netlify.app/docs/basic/getting-started/forward_and_create_ref/#generic-forwardrefs
// TODO: clean this up when we are able to move to React 19, which no longer requires forwardRef.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const MenuItem = forwardRef<any, BaseMenuItemProps>(MenuItemWithoutForwardRef) as <
  RootComponent extends React.ElementType = DefaultMenuitemRootComponent,
>(
  props: MenuItemProps<RootComponent> & { ref?: PolymorphicRef<RootComponent> }
) => ReturnType<typeof MenuItemWithoutForwardRef>;
